import { X, Minus, Plus } from "lucide-react";
import { useStore } from "@/context/StoreContext";

const ADDONS_FALLBACK = [
  { id: "strap", name: "1 Pair of Adjustable Strap", price: 1, image: "/products/strap-pair.png" },
  { id: "pin", name: "PaMak Pin", price: 2, image: "/products/pamak-pin.png" },
];

function ColorPills({ value, onChange, testbase }) {
  return (
    <div className="color-pills">
      {["Pink", "Black"].map((c) => (
        <button
          key={c}
          type="button"
          className={`color-pill ${value === c ? "on" : ""}`}
          data-testid={`${testbase}-${c.toLowerCase()}`}
          onClick={() => onChange(c)}
        >
          <span className={`dot ${c.toLowerCase()}`} /> {c}
        </button>
      ))}
    </div>
  );
}

function AddonRow({ line, addon, t }) {
  const { changeAddonQty } = useStore();
  return (
    <div className="addon-row" data-testid={`addon-row-${addon.id}`}>
      <img src={addon.image} alt="" />
      <div className="addon-info">
        <span>{addon.name}</span>
        <small>${addon.price.toFixed(2)}</small>
      </div>
      <div className="qty">
        <button data-testid={`addon-minus-${addon.id}`} aria-label="Decrease" onClick={() => changeAddonQty(line.key, addon.id, -1)}><Minus size={11} /></button>
        <span data-testid={`addon-qty-${addon.id}`}>{addon.qty}</span>
        <button data-testid={`addon-plus-${addon.id}`} aria-label="Increase" onClick={() => changeAddonQty(line.key, addon.id, 1)}><Plus size={11} /></button>
      </div>
    </div>
  );
}

export default function CartDrawer() {
  const {
    cart, cartOpen, setCartOpen, changeQty, removeLine, changeColor, setOption,
    addAddon, toast, cartTotal, money, t, displayName, setCheckoutOpen, products, config, cartCount,
  } = useStore();

  const addonsCatalog = (config && Array.isArray(config.addons) && config.addons.length > 0)
    ? config.addons
    : ADDONS_FALLBACK;

  const checkout = () => {
    if (cart.length === 0) {
      toast(t("toast.emptyCart"));
      return;
    }
    setCartOpen(false);
    setCheckoutOpen(true);
  };

  const siblingWith = (line, color) => products.find((p) => p.name === line.name && p.color === color);
  const testId = (s) => s.replace(/[^a-zA-Z0-9]/g, "");

  // v11: cart add-ons show the same image + name as the Shop's Add-ons section (i18n-aware).
  const addonDisplay = (a) => {
    const pid = a.id === "strap" ? "adjustable-strap" : a.id === "pin" ? "pamak-pin" : a.id;
    const prod = products.find((p) => p.id === pid);
    return prod ? { ...a, name: t(`name.${prod.group}`), image: prod.image } : a;
  };

  return (
    <aside className={`cart-drawer ${cartOpen ? "open" : ""}`} data-testid="cart-drawer" aria-hidden={!cartOpen}>
      <div className="cart-header">
        <div>
          <p className="section-title-small">{t("cart.eyebrow")}</p>
          <h2>{t("cart.title")} {cartCount > 0 && <span className="cart-n">({cartCount})</span>}</h2>
        </div>
        <button className="close-btn" data-testid="cart-drawer-close" aria-label="Close cart" onClick={() => setCartOpen(false)}>
          <X size={18} />
        </button>
      </div>

      <div className="cart-items" data-lenis-prevent data-testid="cart-items">
        {cart.length === 0 ? (
          <div className="empty-cart" data-testid="cart-empty">
            {t("cart.empty")}
            <br />
            <br />
            {t("cart.empty2")}
          </div>
        ) : (
          cart.map((line) => (
            <div className="cart-item" key={line.key} data-testid={`cart-item-${line.productId}`}>
              <img src={line.image} alt={displayName(line)} />
              <div className="cart-item-body">
                <h4>{displayName(line)}</h4>

                {line.group === "set" ? (
                  <div className="set-options">
                    <div className="set-opt">
                      <small>{t("opt.knee")}</small>
                      <ColorPills
                        value={line.options?.knee || "Pink"}
                        onChange={(c) => setOption(line.key, "knee", c)}
                        testbase={`bag-set-knee-${testId(line.key)}`}
                      />
                    </div>
                    <div className="set-opt">
                      <small>{t("opt.bag")}</small>
                      <ColorPills
                        value={line.options?.bag || "Pink"}
                        onChange={(c) => setOption(line.key, "bag", c)}
                        testbase={`bag-set-bag-${testId(line.key)}`}
                      />
                    </div>
                    <small className="product-note" data-testid="set-includes-note">✿ {t("set.includes")}</small>
                  </div>
                ) : (
                  <div className="set-opt">
                    <small>{t("cart.color")}</small>
                    <ColorPills
                      value={line.color}
                      onChange={(c) => {
                        const sibling = siblingWith(line, c);
                        if (sibling) changeColor(line.key, sibling);
                      }}
                      testbase={`bag-color-${line.productId}`}
                    />
                  </div>
                )}

                <small className="cart-price">{money(line.price)} × {line.qty}</small>

                <div className="qty">
                  <button data-testid={`cart-item-decrement-${line.productId}`} aria-label="Decrease quantity" onClick={() => changeQty(line.key, -1)}>
                    <Minus size={12} />
                  </button>
                  <span data-testid={`cart-item-qty-${line.productId}`}>{line.qty}</span>
                  <button data-testid={`cart-item-increment-${line.productId}`} aria-label="Increase quantity" onClick={() => changeQty(line.key, 1)}>
                    <Plus size={12} />
                  </button>
                </div>

                <div className="addon-block" data-testid={`addons-${line.productId}`}>
                  {(line.addons || []).map((a) => (
                    <AddonRow key={a.id} line={line} addon={addonDisplay(a)} t={t} />
                  ))}
                  {addonsCatalog
                    .filter((a) => !(line.addons || []).some((x) => x.id === a.id))
                    .map((a) => {
                      const d = addonDisplay(a);
                      return (
                        <button
                          key={a.id}
                          className="addon-add"
                          data-testid={`addon-add-${line.productId}-${a.id}`}
                          onClick={() => addAddon(line.key, a)}
                        >
                          <Plus size={12} /> {t("addon.add")} {d.name} — {money(d.price)}
                        </button>
                      );
                    })}
                </div>
              </div>
              <button className="remove" data-testid={`cart-item-remove-${line.productId}`} aria-label="Remove item" onClick={() => removeLine(line.key)}>
                <X size={16} />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="cart-total">
        <div className="total-row">
          <span>{t("cart.subtotal")}</span>
          <span data-testid="cart-subtotal">{money(cartTotal)}</span>
        </div>
        <div className="total-row">
          <span>{t("cart.delivery")}</span>
          <span data-testid="cart-delivery">{t("cart.free")}</span>
        </div>
        <div className="total-row main">
          <span>{t("cart.total")}</span>
          <span data-testid="cart-total">{money(cartTotal)}</span>
        </div>
        <button className="checkout-btn" data-testid="cart-checkout-button" onClick={checkout}>
          {t("cart.checkout")}
        </button>
      </div>
    </aside>
  );
}
